import React, { useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { FaCheck } from 'react-icons/fa'
import LogoImage from '../img/logo.webp'
import PlusPill from './plus_pill'
import { useTranslation } from '../lib/i18n'
import { getLocale, formatPrice, yearlySavingPercent } from '../lib/locale'
import useStretchConfig from '../home/stretches/config'
import { trackCheckout } from '../lib/tracking'

const LS_CHECKOUT = 'https://keybreak.lemonsqueezy.com/checkout/buy'
const isProd = import.meta.env.PROD
const MONTHLY_ID = isProd ? '2968c751-cba3-4bb6-ba20-b60028fd7314' : '4e97b79f-3815-46bc-aab5-1edf662d444d'
const YEARLY_ID = isProd ? '59387f1a-d69e-4f66-808c-36a4f439e7d5' : 'a7c3561c-5ef1-41d5-ba68-40e46b0e2fe8'

const CHECKOUT_MONTHLY = `${LS_CHECKOUT}/${MONTHLY_ID}?embed=1`
const CHECKOUT_YEARLY = `${LS_CHECKOUT}/${YEARLY_ID}?embed=1`

export const CHECKOUT_URLS = { monthly: CHECKOUT_MONTHLY, yearly: CHECKOUT_YEARLY }

export const launchCheckout = (plan, quantity = 1) => {
    let url = CHECKOUT_URLS[plan]
    if (quantity > 1) url += `&quantity=${quantity}`
    trackCheckout(plan)
    if (window.LemonSqueezy) {
        window.LemonSqueezy.Url.Open(url)
    } else {
        window.open(url, '_blank')
    }
}

const LogoWrapper = styled.div`
    position: absolute;
    top: 60px;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
`

const Logo = styled.img`
    width: 80px;
    height: 80px;
`

const LogoBadge = styled(PlusPill)`
    position: absolute;
    bottom: -4px;
    right: -8px;
    margin-left: 0;
    font-size: 10px;
    padding: 3px 8px;
    border-radius: 6px;
    line-height: 1;
`

const CarouselContainer = styled.div`
    position: relative;
    overflow: hidden;
    ${({ $standalone }) => $standalone
        ? 'margin: 0 0 40px; width: 100vw; margin-left: calc(-50vw + 50%);'
        : 'margin: -2rem -2rem 40px;'}
`

const scrollLeft = keyframes`
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
`

const CarouselWrapper = styled.div`
    position: relative;
    z-index: 1;
    pointer-events: none;
`

const CarouselTrack = styled.div`
    display: flex;
    gap: 10px;
    width: max-content;
    animation: ${scrollLeft} 360s linear infinite;

    &:hover {
        animation-play-state: paused;
    }
`

const CarouselImage = styled.img`
    width: 160px;
    height: 90px;
    object-fit: cover;
    border-radius: 8px;
    flex-shrink: 0;
    filter: grayscale(100%) brightness(0.7);
    opacity: 0.6;
`

const Hero = styled.div`
    text-align: center;
    margin-bottom: 24px;
`

const Headline = styled.h2`
    font-size: 24px;
    font-weight: 600;
    color: #fff;
    margin: 0;
`

const FeatureList = styled.ul`
    list-style: none;
    margin: 0 0 24px;
    padding: 0;
`

const FeatureItem = styled.li`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 0;
    font-size: 14px;
    color: #ccc;

    svg {
        color: ${({ theme }) => theme.palette.brand.blue};
        flex-shrink: 0;
        font-size: 14px;
        width: 20px;
    }
`

const PricingStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`

const SelectableCard = styled.div`
    background: #22262e;
    border: 2px solid ${({ $selected, theme }) => $selected ? theme.palette.brand.blue : '#333'};
    border-radius: 12px;
    padding: 16px 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${({ $selected, theme }) => $selected ? theme.palette.brand.blue : '#444'};
    }
`

const RadioCircle = styled.div`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid ${({ $selected, theme }) => $selected ? theme.palette.brand.blue : '#555'};
    margin-right: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &::after {
        content: '';
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: ${({ theme }) => theme.palette.brand.blue};
        display: ${({ $selected }) => $selected ? 'block' : 'none'};
    }
`

const PlanInfo = styled.div`
    flex: 1;
`

const PlanHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
`

const PlanTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: #fff;
`

const PlanSubtitle = styled.div`
    font-size: 13px;
    color: #888;
`

const PlanPriceContainer = styled.div`
    text-align: right;
`

const PlanPrice = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: #fff;
`

const PlanEquivalent = styled.div`
    font-size: 12px;
    color: #888;
`

const PlanBadge = styled.div`
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 6px;
    text-transform: uppercase;
    background: ${({ $color, theme }) => $color === 'blue' ? theme.palette.brand.blue : theme.palette.greyScale.grey};
    color: white;
`

const MainCTA = styled.button`
    width: 100%;
    padding: 16px;
    margin-top: 24px;
    background: ${({ theme }) => theme.palette.brand.blue};
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        opacity: 0.9;
    }
    
    &:active {
        transform: translateY(1px);
    }
`

const SuccessContainer = styled.div`
    text-align: center;
    padding: 40px 20px;
`

const SuccessHeadline = styled.h2`
    font-size: 24px;
    font-weight: 600;
    color: #fff;
    margin: 0 0 12px;
`

const SuccessText = styled.p`
    font-size: 15px;
    color: #aaa;
    margin: 0 0 24px;
    line-height: 1.5;
`

const DoneButton = styled.button`
    padding: 12px 32px;
    background: ${({ theme }) => theme.palette.brand.blue};
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.9;
    }
`

const SuccessLogo = styled.div`
    position: relative;
    display: inline-block;
    margin-bottom: 24px;
`

export const Carousel = ({ standalone }) => {
    const stretchConfig = useStretchConfig()

    return (
        <CarouselContainer $standalone={standalone}>
            <CarouselWrapper>
                <CarouselTrack>
                    {Array(8).fill(stretchConfig).flat().map((s, i) => (
                        <CarouselImage key={i} src={s.image} alt={s.title} />
                    ))}
                </CarouselTrack>
            </CarouselWrapper>
        </CarouselContainer>
    )
}

export const UpgradeView = ({ onCheckout, standalone, locale, isAppEmbed }) => {
    const [selectedPlan, setSelectedPlan] = useState('monthly');
    const { t } = useTranslation(undefined, { keyPrefix: 'plus' })
    const pricing = locale || getLocale()
    const yearlyPrice = formatPrice(pricing, pricing.yearly)

    return (
        <>
            <LogoWrapper>
                <Logo src={LogoImage} alt="Keybreak" />
                <LogoBadge>Plus</LogoBadge>
            </LogoWrapper>
            <Carousel standalone={standalone} />

            {!isAppEmbed && (
                <Hero>
                    <Headline>{t('headline')}</Headline>
                </Hero>
            )}

            <FeatureList>
                {t('features', { returnObjects: true }).map((f) => (
                    <FeatureItem key={f}>
                        <FaCheck />
                        {f}
                    </FeatureItem>
                ))}
            </FeatureList>

            <PricingStack>
                <SelectableCard $selected={selectedPlan === 'yearly'} onClick={() => setSelectedPlan('yearly')}>
                    <RadioCircle $selected={selectedPlan === 'yearly'} />
                    <PlanInfo>
                        <PlanHeader>
                            <PlanTitle>{t('yearly')}</PlanTitle>
                            <PlanBadge $color="grey">{t('save', { percent: yearlySavingPercent(pricing) })}</PlanBadge>
                        </PlanHeader>
                        <PlanSubtitle>{t('billedYearly', { price: yearlyPrice })}</PlanSubtitle>
                    </PlanInfo>
                    <PlanPriceContainer>
                        <PlanPrice>{formatPrice(pricing, pricing.yearly / 12)}</PlanPrice>
                        <PlanEquivalent>{t('perMonth')}</PlanEquivalent>
                    </PlanPriceContainer>
                </SelectableCard>

                <SelectableCard $selected={selectedPlan === 'monthly'} onClick={() => setSelectedPlan('monthly')}>
                    <RadioCircle $selected={selectedPlan === 'monthly'} />
                    <PlanInfo>
                        <PlanHeader>
                            <PlanTitle>{t('monthly')}</PlanTitle>
                            <PlanBadge $color="blue">{t('firstMonthFree')}</PlanBadge>
                        </PlanHeader>
                        <PlanSubtitle>{t('cancelAnytime')}</PlanSubtitle>
                    </PlanInfo>
                    <PlanPriceContainer>
                        <PlanPrice>{formatPrice(pricing, pricing.monthly)}</PlanPrice>
                        <PlanEquivalent>{t('perMonth')}</PlanEquivalent>
                    </PlanPriceContainer>
                </SelectableCard>
            </PricingStack>

            <MainCTA onClick={onCheckout(selectedPlan === 'monthly' ? CHECKOUT_MONTHLY : CHECKOUT_YEARLY, selectedPlan)}>
                {selectedPlan === 'monthly' ? t('startTrial') : t('upgradeYearly', { price: yearlyPrice })}
            </MainCTA>
        </>
    );
};

export const SuccessView = ({ onClose }) => {
    const { t } = useTranslation(undefined, { keyPrefix: 'plus' })

    return (
        <SuccessContainer>
            <SuccessLogo>
                <Logo src={LogoImage} alt="Keybreak" />
                <LogoBadge>Plus</LogoBadge>
            </SuccessLogo>
            <SuccessHeadline>{t('successHeadline')}</SuccessHeadline>
            <SuccessText>
                {t('successLine1')}<br />
                {t('successLine2')}
            </SuccessText>
            {onClose && <DoneButton onClick={onClose}>{t('done')}</DoneButton>}
        </SuccessContainer>
    )
}
